<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
import { crudMethods } from '@state/helpers'

export default {
  page: {
    title: 'Master Pengaturan',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { Layout },
  data() {
    return {
      items: [],
      search: '',
      loading: false,
      loadError: '',
      fields: [
        { key: 'nama', label: 'Pengaturan', thStyle: { width: '280px' } },
        { key: 'nilai', label: 'Nilai' },
        { key: 'detail_nilai', label: 'Keterangan' },
        { key: 'aksi', label: 'Aksi', thStyle: { width: '190px' } },
      ],
    }
  },
  computed: {
    canManage() {
      const user = this.$store.state.auth.currentUser
      const permission =
        user && user.permissions && user.permissions['master.pengaturan']
      return !!(permission && permission.view && permission.manage)
    },
    filteredItems() {
      const query = this.search.trim().toLowerCase()
      return this.items.filter((item) =>
        [item.nama, item.nilai, item.detail_nilai].some((value) =>
          String(value || '')
            .toLowerCase()
            .includes(query)
        )
      )
    },
    hasUnsavedChanges() {
      return this.items.some((item) => item.editing)
    },
  },
  mounted() {
    this.loadSettings()
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedChanges) return next()
    this.$bvModal
      .msgBoxConfirm(
        'Masih ada pengaturan yang belum disimpan. Tinggalkan halaman?',
        {
          title: 'Perubahan belum disimpan',
          okTitle: 'Tinggalkan',
          cancelTitle: 'Tetap di sini',
          centered: true,
        }
      )
      .then((confirmed) => next(confirmed ? undefined : false))
      .catch(() => next(false))
  },
  methods: {
    ...crudMethods,
    async loadSettings() {
      this.loading = true
      this.loadError = ''
      try {
        const response = await this.listData({ url: '/web/master/pengaturan' })
        if (!response || !response.data.success)
          throw new Error('Data tidak tersedia')
        this.items = response.data.data.map((item) => ({
          ...item,
          editing: false,
          saving: false,
          draftValue: item.nilai || '',
          draftDetail: item.detail_nilai || '',
          error: '',
        }))
      } catch (error) {
        this.loadError = 'Gagal memuat pengaturan. Silakan coba lagi.'
      } finally {
        this.loading = false
      }
    },
    edit(item) {
      item.draftValue = item.nilai || ''
      item.draftDetail = item.detail_nilai || ''
      item.error = ''
      item.editing = true
      this.$nextTick(() => {
        const input = this.$el.querySelector(`[data-setting-id="${item.id}"]`)
        if (input) input.focus()
      })
    },
    cancel(item) {
      if (item.saving) return
      item.editing = false
      item.error = ''
    },
    async save(item) {
      if (item.saving || !this.canManage) return
      item.saving = true
      item.error = ''
      try {
        const response = await this.updateData({
          url: `/web/master/pengaturan/${encodeURIComponent(item.id)}`,
          body: { nilai: item.draftValue, detail_nilai: item.draftDetail },
          throwOnError: true,
        })
        if (!response || !response.data.success)
          throw new Error('Pengaturan gagal disimpan.')
        item.nilai = response.data.data.nilai
        item.detail_nilai = response.data.data.detail_nilai
        item.editing = false
        this.$bvToast.toast('Pengaturan berhasil disimpan.', {
          title: item.nama,
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
        })
      } catch (error) {
        const data = error.response && error.response.data
        item.error =
          data && data.errors
            ? Object.values(data.errors)
                .reduce((all, messages) => all.concat(messages), [])
                .join(' ')
            : 'Pengaturan gagal disimpan. Silakan coba lagi.'
      } finally {
        item.saving = false
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div class="card mt-4 settings-page">
      <div class="card-body">
        <div class="settings-heading">
          <div>
            <h4 class="header-title mb-1">Pengaturan</h4>
            <p class="text-muted mb-0"
              >Kelola nilai pengaturan aplikasi langsung di tabel. Klik Edit,
              ubah nilai, lalu Simpan.</p
            >
          </div>
          <div class="settings-search"
            ><i class="uil uil-search" aria-hidden="true"></i
            ><input
              v-model="search"
              type="search"
              class="form-control"
              placeholder="Cari pengaturan..."
              aria-label="Cari pengaturan"
          /></div>
        </div>
        <div v-if="loadError" class="alert alert-danger" role="alert"
          >{{ loadError }}
          <button
            type="button"
            class="btn btn-sm btn-outline-danger ml-2"
            @click="loadSettings"
            >Coba lagi</button
          ></div
        >
        <div
          class="settings-table-wrapper"
          tabindex="0"
          role="region"
          aria-label="Tabel pengaturan, dapat digulir"
        >
          <b-table
            :items="filteredItems"
            :fields="fields"
            :busy="loading"
            primary-key="id"
            class="settings-table mb-0"
            show-empty
            hover
            no-border-collapse
            empty-text="Tidak ada pengaturan yang sesuai."
          >
            <template v-slot:table-busy
              ><div class="py-4 text-center text-primary"
                ><b-spinner small class="mr-2"></b-spinner>Memuat
                pengaturan...</div
              ></template
            >
            <template v-slot:cell(nama)="{ item }"
              ><strong>{{ item.nama.replace(/_/g, ' ') }}</strong
              ><small class="d-block text-muted mt-1">{{
                item.nama
              }}</small></template
            >
            <template v-slot:cell(nilai)="{ item }">
              <textarea
                v-if="item.editing"
                v-model="item.draftValue"
                :data-setting-id="item.id"
                class="form-control form-control-sm"
                rows="2"
                maxlength="255"
                :disabled="item.saving"
                :aria-label="`Nilai ${item.nama}`"
              ></textarea>
              <span v-else class="settings-value">{{ item.nilai || '—' }}</span>
              <small
                v-if="item.tipe === 'GAMBAR'"
                class="d-block text-muted mt-1"
                >Nilai berupa alamat atau path gambar.</small
              >
              <small
                v-if="item.error"
                class="text-danger d-block mt-1"
                role="alert"
                >{{ item.error }}</small
              >
            </template>
            <template v-slot:cell(detail_nilai)="{ item }">
              <textarea
                v-if="item.editing"
                v-model="item.draftDetail"
                class="form-control form-control-sm"
                rows="2"
                maxlength="255"
                :disabled="item.saving"
                :aria-label="`Keterangan ${item.nama}`"
              ></textarea>
              <span v-else class="settings-value text-muted">{{
                item.detail_nilai || '—'
              }}</span>
            </template>
            <template v-slot:cell(aksi)="{ item }">
              <div v-if="canManage" class="settings-actions">
                <template v-if="item.editing">
                  <button
                    type="button"
                    class="btn btn-sm btn-primary"
                    :disabled="item.saving"
                    @click="save(item)"
                    ><b-spinner v-if="item.saving" small></b-spinner
                    ><span v-else>Simpan</span></button
                  >
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="item.saving"
                    @click="cancel(item)"
                    >Batal</button
                  >
                </template>
                <button
                  v-else
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="edit(item)"
                  ><i class="uil uil-edit-alt mr-1"></i>Edit</button
                >
              </div>
              <span v-else class="text-muted small">Hanya lihat</span>
            </template>
          </b-table>
        </div>
        <p class="text-muted small mt-3 mb-0"
          >{{ filteredItems.length }} pengaturan<span
            v-if="hasUnsavedChanges"
            class="text-warning ml-3"
            >Ada perubahan yang belum disimpan.</span
          ></p
        >
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.settings-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}
.settings-search {
  position: relative;
  width: 320px;
  max-width: 100%;
}
.settings-search i {
  position: absolute;
  top: 9px;
  left: 12px;
  color: #8a8f98;
  font-size: 18px;
}
.settings-search input {
  padding-left: 38px;
}
.settings-table-wrapper {
  overflow: auto;
  max-height: 65vh;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  isolation: isolate;
}
::v-deep .settings-table {
  width: 100%;
  min-width: 1000px;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
::v-deep .settings-table th,
::v-deep .settings-table td {
  padding: 12px;
  vertical-align: middle;
  border-top: 0;
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  background: #fff;
  overflow-wrap: anywhere;
}
::v-deep .settings-table th {
  position: sticky;
  top: 0;
  z-index: 20;
  background: #f8f9fa;
  color: #495057;
  font-weight: 700;
}
::v-deep .settings-table th:first-child,
::v-deep .settings-table td:first-child {
  position: sticky;
  left: 0;
  z-index: 10;
  box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.2);
}
::v-deep .settings-table th:last-child,
::v-deep .settings-table td:last-child {
  position: sticky;
  right: 0;
  z-index: 11;
  box-shadow: -4px 0 6px -4px rgba(0, 0, 0, 0.2);
}
::v-deep .settings-table thead th:first-child,
::v-deep .settings-table thead th:last-child {
  z-index: 30;
  background: #f8f9fa;
}
::v-deep .settings-table tbody tr:hover td {
  background: #f8f9fa;
}
.settings-value {
  white-space: pre-wrap;
}
.settings-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
@media (max-width: 767px) {
  ::v-deep .settings-table td:first-child {
    position: static;
    box-shadow: none;
  }
  ::v-deep .settings-table th:first-child {
    left: auto;
  }
}
</style>
